'use client'

import { useEffect, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import type { ReactNode } from 'react'

import {
  initializeActivityEventClient,
  enqueueActivityEvent,
} from '@/lib/activity-events'
import {
  initializeTracewayClient,
  recordTracewayNavigation,
} from '@/lib/traceway-breadcrumbs'
import { useAuthStore } from '@/stores/auth-store'

export function TracewayInit({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const hasHydratedFromStorage = useAuthStore((state) => state.hasHydratedFromStorage)
  const authStatus = useAuthStore((state) => state.status)
  const lastRouteRef = useRef<string | null>(null)

  useEffect(() => {
    initializeTracewayClient()
    initializeActivityEventClient()
  }, [])

  useEffect(() => {
    if (!hasHydratedFromStorage || authStatus !== 'authenticated') {
      return
    }

    const query = searchParams.toString()
    const route = query ? `${pathname}?${query}` : pathname
    if (lastRouteRef.current === route) {
      return
    }

    if (lastRouteRef.current === null) {
      recordTracewayNavigation('route-entry', route, {
        pathname,
        query,
      })
      enqueueActivityEvent({
        action: 'route_entry',
        category: 'navigation',
        metadata: {
          pathname,
          query,
        },
        route,
      })
    } else {
      recordTracewayNavigation('route-change', route, {
        from: lastRouteRef.current,
        pathname,
        query,
      })
      enqueueActivityEvent({
        action: 'route_change',
        category: 'navigation',
        metadata: {
          from: lastRouteRef.current,
          pathname,
          query,
        },
        route,
      })
    }

    lastRouteRef.current = route
  }, [authStatus, hasHydratedFromStorage, pathname, searchParams])

  return <>{children}</>
}
